import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined'
import { Box, CircularProgress, FormHelperText, Typography } from '@mui/material'

import { ConfigField } from '@gantt/components/GanttCreateOrEdit/types'

import { GanttSyncLoadingContext } from '../../GanttConfiguration'
import { parseRsqlFields } from '../../helpers'

type GanttPreFilterVariableHelperProps = {
  formPath: string
  rsqlSources: string[]
  objectCode?: string
}

const getRootCharacteristic = (pathStr: string) => pathStr.split('.')[0]

export const GanttPreFilterVariableHelper = ({
  formPath,
  rsqlSources,
  objectCode,
}: GanttPreFilterVariableHelperProps) => {
  const { t } = useTranslation()
  const { watch } = useFormContext()
  const loadingFormPaths = useContext(GanttSyncLoadingContext)
  const isLoading = loadingFormPaths.has(formPath)

  const allFields: ConfigField[] = watch(formPath) ?? []
  const systemFields = allFields.filter(f => f.isSystem)
  const userFields = allFields.filter(f => !f.isSystem)

  const expectedFieldMap = new Map(
    rsqlSources.flatMap(rsql => parseRsqlFields(rsql).map(f => [f.field, f.pathStr]))
  )

  if (!expectedFieldMap.size) return null

  const expectedPathStrs = new Set(expectedFieldMap.values())

  const systemFieldNames = new Set(systemFields.map(f => f.field))
  const userCoveredPathStrs = new Set(
    userFields.filter(f => expectedPathStrs.has(f.pathStr)).map(f => f.pathStr)
  )

  const coveredFields: ConfigField[] = [
    ...systemFields.filter(f => expectedFieldMap.has(f.field)),
    ...userFields.filter(f => expectedPathStrs.has(f.pathStr)),
  ]

  const resolvedRows = [
    ...new Map(
      coveredFields.flatMap(f =>
        (f.pathArray ?? []).map(p => [
          `${p.field}::${p.objectCode}`,
          { field: p.field, objectCode: p.objectCode },
        ])
      )
    ).values(),
  ]

  const unresolvedRoots = [
    ...new Set(
      [...expectedFieldMap.entries()]
        .filter(
          ([field, pathStr]) => !systemFieldNames.has(field) && !userCoveredPathStrs.has(pathStr)
        )
        .map(([, pathStr]) => getRootCharacteristic(pathStr))
    ),
  ]

  // Частично резолвившиеся: pathArray короче pathStr — последний сегмент не найден
  const partiallyUnresolvedRows = coveredFields
    .filter(f => f.isSystem)
    .flatMap(f => {
      const pathSegments = f.pathStr.split('.')
      const resolvedCount = (f.pathArray ?? []).length
      if (resolvedCount < pathSegments.length) {
        return [
          {
            field: pathSegments[resolvedCount],
            objectCode: f.pathArray?.[resolvedCount - 1]?.objectCode ?? '',
          },
        ]
      }

      return []
    })

  const expectedRoots = [...new Set([...expectedFieldMap.values()].map(getRootCharacteristic))]

  if (isLoading) {
    return (
      <Box>
        {expectedRoots.map(rootField => (
          <FormHelperText key={rootField} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={24} />
            <Typography color='text.secondary' variant='body2'>
              {rootField}
            </Typography>
          </FormHelperText>
        ))}
      </Box>
    )
  }

  return (
    <Box>
      {resolvedRows.map(({ field, objectCode: resolvedObjectCode }) => (
        <FormHelperText
          key={`${field}::${resolvedObjectCode}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <CheckCircleIcon color='success' />
          <Typography sx={{ color: '#2e7d32' }} variant='body2'>
            {t('ganttCreate.systemVariableResolved', {
              field,
              objectCode: resolvedObjectCode,
            })}
          </Typography>
        </FormHelperText>
      ))}
      {partiallyUnresolvedRows.map(({ field, objectCode: searchedIn }) => (
        <FormHelperText
          key={`partial::${field}::${searchedIn}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <WarningAmberIcon color='warning' />
          <Typography sx={{ color: '#ed6c02' }} variant='body2'>
            {t('ganttCreate.systemVariableUnresolvedIn', { field, objectCode: searchedIn })}
          </Typography>
        </FormHelperText>
      ))}
      {unresolvedRoots.map(rootField => (
        <FormHelperText key={rootField} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon color='warning' />
          <Typography sx={{ color: '#ed6c02' }} variant='body2'>
            {t(
              objectCode
                ? 'ganttCreate.systemVariableUnresolvedIn'
                : 'ganttCreate.systemVariableUnresolved',
              { field: rootField, objectCode }
            )}
          </Typography>
        </FormHelperText>
      ))}
    </Box>
  )
}
